import { DiscordSDK } from '@discord/embedded-app-sdk';
import { captureError } from '@/sentry';
import { identifyUser, setUserProperties, trackDiscordAuthSuccess, trackDiscordAuthFailure } from '@/services/analytics';

const CLIENT_ID = import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined;
const CANONICAL_ORIGIN = 'https://www.morsehero.io';

const STORAGE_KEYS = {
  accessToken: 'discord.auth.accessToken',
  expiresAt: 'discord.auth.expiresAt',
  scope: 'discord.auth.scope',
  tokenType: 'discord.auth.tokenType',
} as const;

const REQUIRED_SCOPES = ['identify', 'guilds', 'rpc.activities.write'] as const;

const REFRESH_WINDOW_MS = 90_000;

let discordSdk: DiscordSDK | null = null;
let isReady = false;
let readyPromise: Promise<boolean> | null = null;
let readyResolve: ((ready: boolean) => void) | null = null;
let isAuthenticated = false;
let authFailed = false;
let authPromise: Promise<void> | null = null;
let discordAccessToken: string | null = null;
let discordTokenExpiresAt: number | null = null;
let discordTokenScope: string | null = null;
let _discordTokenType: string | null = null;

export type DiscordUser = {
  id: string;
  username: string;
  globalName: string | null;
};

let authenticatedUser: DiscordUser | null = null;

// Auth change listeners
type AuthChangeCallback = () => void;
const authChangeListeners: Set<AuthChangeCallback> = new Set();

function notifyAuthChange(): void {
  for (const callback of authChangeListeners) {
    try {
      callback();
    } catch (err) {
      captureError(err, { scope: 'discord', action: 'authChangeCallback' });
    }
  }
}

/**
 * Subscribe to Discord auth state changes.
 * Returns unsubscribe function.
 */
export function onDiscordAuthChange(callback: AuthChangeCallback): () => void {
  authChangeListeners.add(callback);
  return () => {
    authChangeListeners.delete(callback);
  };
}

type StoredDiscordAuth = {
  accessToken: string;
  expiresAt: number;
  scope: string | null;
  tokenType: string | null;
};

function storageAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readStoredAuth(): StoredDiscordAuth | null {
  if (!storageAvailable()) return null;
  try {
    const accessToken = window.localStorage.getItem(STORAGE_KEYS.accessToken);
    const expiresAtRaw = window.localStorage.getItem(STORAGE_KEYS.expiresAt);
    const expiresAt = expiresAtRaw ? Number(expiresAtRaw) : NaN;
    if (!accessToken || !Number.isFinite(expiresAt)) {
      return null;
    }

    const scope = window.localStorage.getItem(STORAGE_KEYS.scope);
    const tokenType = window.localStorage.getItem(STORAGE_KEYS.tokenType);

    return {
      accessToken,
      expiresAt,
      scope: scope ?? null,
      tokenType: tokenType ?? null,
    };
  } catch (error) {
    captureError(error, { scope: 'discord', action: 'readStoredAuth' });
    return null;
  }
}

function writeStoredAuth(auth: StoredDiscordAuth): void {
  if (!storageAvailable()) return;
  try {
    window.localStorage.setItem(STORAGE_KEYS.accessToken, auth.accessToken);
    window.localStorage.setItem(STORAGE_KEYS.expiresAt, String(auth.expiresAt));
    if (auth.scope) {
      window.localStorage.setItem(STORAGE_KEYS.scope, auth.scope);
    } else {
      window.localStorage.removeItem(STORAGE_KEYS.scope);
    }
    if (auth.tokenType) {
      window.localStorage.setItem(STORAGE_KEYS.tokenType, auth.tokenType);
    } else {
      window.localStorage.removeItem(STORAGE_KEYS.tokenType);
    }
  } catch (error) {
    captureError(error, { scope: 'discord', action: 'writeStoredAuth' });
  }
}

function clearStoredAuth(): void {
  if (!storageAvailable()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEYS.accessToken);
    window.localStorage.removeItem(STORAGE_KEYS.expiresAt);
    window.localStorage.removeItem(STORAGE_KEYS.scope);
    window.localStorage.removeItem(STORAGE_KEYS.tokenType);
  } catch (error) {
    captureError(error, { scope: 'discord', action: 'clearStoredAuth' });
  }
}

function applyStoredAuth(auth: StoredDiscordAuth): void {
  const tokenChanged = discordAccessToken !== auth.accessToken;
  discordAccessToken = auth.accessToken;
  discordTokenExpiresAt = auth.expiresAt;
  discordTokenScope = auth.scope;
  _discordTokenType = auth.tokenType;
  if (tokenChanged) {
    isAuthenticated = false;
    authenticatedUser = null;
  }
}

function clearAuthState(clearStorage: boolean): void {
  if (clearStorage) {
    clearStoredAuth();
  }
  discordAccessToken = null;
  discordTokenExpiresAt = null;
  discordTokenScope = null;
  _discordTokenType = null;
  isAuthenticated = false;
  authenticatedUser = null;
}

function shouldRefreshToken(expiresAt: number | null): boolean {
  if (!expiresAt) return true;
  return Date.now() >= expiresAt - REFRESH_WINDOW_MS;
}

function hasRequiredScopes(scope: string | null): boolean {
  if (!scope) return false;
  const parts = scope.split(' ').map((part) => part.trim()).filter(Boolean);
  return REQUIRED_SCOPES.every((required) => parts.includes(required));
}

if (storageAvailable()) {
  const stored = readStoredAuth();
  if (stored && hasRequiredScopes(stored.scope)) {
    applyStoredAuth(stored);
  } else if (stored) {
    clearAuthState(true);
  }

  window.addEventListener('storage', (event) => {
    if (event.key && !Object.values(STORAGE_KEYS).includes(event.key as typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS])) {
      return;
    }

    const next = readStoredAuth();
    if (next) {
      applyStoredAuth(next);
    } else {
      clearAuthState(false);
    }
  });
}

// Internal session state tracking
type DiscordSession =
  | { kind: 'idle' }
  | { kind: 'levelSelect' }
  | { kind: 'solo'; difficulty: string; messageNumber: number; startTime: number }
  | { kind: 'soloResults' }
  | { kind: 'daily'; startTime: number }
  | { kind: 'dailyResults'; score: number }
  | {
      kind: 'lobby';
      mode: 'coop' | 'telephone';
      difficulty: string;
      roomId: string;
      playerCount: number;
    }
  | {
      kind: 'match';
      mode: 'coop' | 'telephone';
      difficulty: string;
      roomId: string;
      playerCount: number;
      maxPlayers: number;
      playerNames: string[];
      startTime: number;
    }
  | {
      kind: 'multiplayerResults';
      mode: 'coop' | 'telephone';
      roomId: string;
      playerCount: number;
    };

let currentSession: DiscordSession | null = null;

export type DiscordLobbyPresenceOpts = {
  mode: 'coop' | 'telephone';
  difficulty: string;
  roomId: string;
  playerCount: number;
};

export type DiscordMatchPresenceOpts = {
  mode: 'coop' | 'telephone';
  difficulty: string;
  roomId: string;
  playerCount: number;
  maxPlayers: number;
  playerNames: string[];
};

export type DiscordMultiplayerResultsPresenceOpts = {
  mode: 'coop' | 'telephone';
  roomId: string;
  playerCount: number;
};

export type DiscordSoloPresenceOpts = {
  difficulty: string;
  messageNumber: number;
};

export type DiscordSoloResultsPresenceOpts = Record<string, never>;

/**
 * Initialize Discord SDK. Must be called early, before game creation.
 */
export function initDiscord(): void {
  if (!CLIENT_ID) {
    if (import.meta.env.DEV) {
      console.log('Discord client ID not configured, presence disabled');
    }
    return;
  }

  readyPromise = new Promise((resolve) => {
    readyResolve = resolve;
  });

  try {
    discordSdk = new DiscordSDK(CLIENT_ID);
    void discordSdk.ready().then(() => {
      isReady = true;
      if (import.meta.env.DEV) {
        console.log('Discord SDK ready');
      }
      if (readyResolve) readyResolve(true);
      if (currentSession) void syncPresence();
    }).catch((err) => {
      captureError(err, { scope: 'discord', action: 'initDiscord.ready' });
      if (import.meta.env.DEV) {
        console.log('Discord SDK failed to initialize:', err);
      }
      discordSdk = null;
      isReady = false;
      isAuthenticated = false;
      authFailed = false;
      if (readyResolve) readyResolve(false);
    });
  } catch (err) {
    captureError(err, { scope: 'discord', action: 'initDiscord' });
    if (import.meta.env.DEV) {
      console.log('Discord SDK creation failed:', err);
    }
    discordSdk = null;
    isReady = false;
    isAuthenticated = false;
    authFailed = false;
    if (readyResolve) readyResolve(false);
  }
}

export function isDiscordReady(): boolean {
  return isReady && discordSdk !== null;
}

export async function waitForDiscordReady(): Promise<boolean> {
  if (isReady) return true;
  if (!readyPromise) return false;
  return readyPromise;
}

export function getDiscordClientId(): string | undefined {
  return CLIENT_ID;
}

export function getDiscordInstanceId(): string | null {
  if (discordSdk?.instanceId) return discordSdk.instanceId;
  if (import.meta.env.DEV && import.meta.env.VITE_FORCE_DISCORD_ACTIVITY === '1') return 'dev-instance';
  return null;
}

export function getDiscordGuildId(): string | null {
  if (import.meta.env.DEV && import.meta.env.VITE_FORCE_GUILD_ID) {
    return String(import.meta.env.VITE_FORCE_GUILD_ID);
  }
  return discordSdk?.guildId ?? null;
}

export function getDiscordUserId(): string | null {
  return authenticatedUser?.id ?? null;
}

export function getDiscordDisplayName(): string | null {
  return authenticatedUser?.globalName || authenticatedUser?.username || null;
}

export function getDiscordAccessToken(): string | null {
  return discordAccessToken;
}

export async function ensureDiscordAccessToken(): Promise<string | null> {
  const authed = await ensureDiscordAuthenticated();
  if (!authed) return null;
  return discordAccessToken;
}

export async function ensureDiscordUser(): Promise<DiscordUser | null> {
  const authed = await ensureDiscordAuthenticated();
  if (!authed) return null;
  return authenticatedUser;
}

/**
 * Set Discord Rich Presence for multiplayer lobby.
 */
export function setDiscordLobbyPresence(opts: DiscordLobbyPresenceOpts): void {
  currentSession = {
    kind: 'lobby',
    mode: opts.mode,
    difficulty: opts.difficulty,
    roomId: opts.roomId,
    playerCount: opts.playerCount,
  };
  void syncPresence();
}

/**
 * Set Discord Rich Presence for multiplayer match.
 */
export function setDiscordMatchPresence(opts: DiscordMatchPresenceOpts): void {
  currentSession = {
    kind: 'match',
    mode: opts.mode,
    difficulty: opts.difficulty,
    roomId: opts.roomId,
    playerCount: opts.playerCount,
    maxPlayers: opts.maxPlayers,
    playerNames: opts.playerNames,
    startTime: Date.now(),
  };
  void syncPresence();
}

/**
 * Set Discord Rich Presence for multiplayer results.
 */
export function setDiscordMultiplayerResultsPresence(opts: DiscordMultiplayerResultsPresenceOpts): void {
  currentSession = {
    kind: 'multiplayerResults',
    mode: opts.mode,
    roomId: opts.roomId,
    playerCount: opts.playerCount,
  };
  void syncPresence();
}

export function setDiscordIdlePresence(): void {
  currentSession = { kind: 'idle' };
  void syncPresence();
}

export function setDiscordLevelSelectPresence(): void {
  currentSession = { kind: 'levelSelect' };
  void syncPresence();
}

export function setDiscordSoloPresence(opts: DiscordSoloPresenceOpts): void {
  currentSession = {
    kind: 'solo',
    difficulty: opts.difficulty,
    messageNumber: opts.messageNumber,
    startTime: Date.now(),
  };
  void syncPresence();
}

export function setDiscordSoloResultsPresence(_opts?: DiscordSoloResultsPresenceOpts): void {
  currentSession = { kind: 'soloResults' };
  void syncPresence();
}

export function setDiscordDailyPresence(): void {
  currentSession = { kind: 'daily', startTime: Date.now() };
  void syncPresence();
}

export function setDiscordDailyResultsPresence(score: number): void {
  currentSession = { kind: 'dailyResults', score };
  void syncPresence();
}

/**
 * Clear Discord Rich Presence activity by setting to idle.
 * Note: setActivity({ activity: null }) causes SDK parse errors, so we set idle instead.
 */
export function clearDiscordPresence(): void {
  setDiscordIdlePresence();
}

type TokenPayload = {
  accessToken: string;
  tokenType: string | null;
  scope: string | null;
  expiresAt: number;
};

function parseTokenPayload(payload: unknown): TokenPayload | null {
  if (!payload || typeof payload !== 'object') return null;
  const data = payload as {
    access_token?: unknown;
    token_type?: unknown;
    expires_in?: unknown;
    scope?: unknown;
  };
  const accessToken = typeof data.access_token === 'string' ? data.access_token : null;
  const expiresIn = typeof data.expires_in === 'number' ? data.expires_in : null;
  if (!accessToken || !expiresIn) return null;

  return {
    accessToken,
    tokenType: typeof data.token_type === 'string' ? data.token_type : null,
    scope: typeof data.scope === 'string' ? data.scope : null,
    expiresAt: Date.now() + expiresIn * 1000,
  };
}

function applyTokenPayload(payload: TokenPayload): void {
  discordAccessToken = payload.accessToken;
  discordTokenExpiresAt = payload.expiresAt;
  discordTokenScope = payload.scope ?? discordTokenScope;
  _discordTokenType = payload.tokenType;
  writeStoredAuth({
    accessToken: payload.accessToken,
    expiresAt: payload.expiresAt,
    scope: payload.scope ?? discordTokenScope,
    tokenType: payload.tokenType,
  });
}

function createSupabaseUserInBackground(accessToken: string): void {
  fetch('/api/auth/discord', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ accessToken }),
  }).catch((error) => {
    captureError(error, { scope: 'discord', action: 'createSupabaseUserInBackground' });
  });
}

async function authenticateWithAccessToken(accessToken: string): Promise<void> {
  if (!discordSdk) {
    throw new Error('Discord SDK not initialized');
  }

  const auth = await discordSdk.commands.authenticate({ access_token: accessToken });
  authenticatedUser = {
    id: auth.user.id,
    username: auth.user.username,
    globalName: auth.user.global_name ?? null,
  };
  discordAccessToken = accessToken;
  isAuthenticated = true;
  authFailed = false;
  notifyAuthChange();

  // Track successful Discord auth
  identifyUser(auth.user.id);
  setUserProperties({ discord_linked: true });
  trackDiscordAuthSuccess();

  // Create Supabase user in background (fire-and-forget)
  void createSupabaseUserInBackground(accessToken);
}

async function refreshDiscordAccessToken(): Promise<boolean> {
  try {
    const response = await fetch('/api/discord/refresh', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      credentials: 'include',
    });
    if (!response.ok) return false;
    const payload = parseTokenPayload(await response.json());
    if (!payload) return false;
    if (payload.scope && !hasRequiredScopes(payload.scope)) return false;
    applyTokenPayload(payload);
    return true;
  } catch (error) {
    captureError(error, { scope: 'discord', action: 'refreshDiscordAccessToken' });
    return false;
  }
}

async function ensureDiscordAuthenticated(): Promise<boolean> {
  if (!discordSdk || !isReady) return false;
  if (authFailed) return false;

  if (isAuthenticated && discordAccessToken && !shouldRefreshToken(discordTokenExpiresAt)) {
    return true;
  }

  if (authPromise) {
    try {
      await authPromise;
    } catch (error) {
      captureError(error, { scope: 'discord', action: 'ensureDiscordAuthenticated' });
    }
    return isAuthenticated;
  }

  authPromise = (async () => {
    try {
      const stored = readStoredAuth();
      if (stored && hasRequiredScopes(stored.scope)) {
        applyStoredAuth(stored);
      } else if (stored) {
        clearAuthState(true);
      }

      if (discordAccessToken && shouldRefreshToken(discordTokenExpiresAt)) {
        const refreshed = await refreshDiscordAccessToken();
        if (!refreshed) {
          clearAuthState(true);
        }
      }

      if (discordAccessToken && !shouldRefreshToken(discordTokenExpiresAt)) {
        if (!isAuthenticated) {
          await authenticateWithAccessToken(discordAccessToken);
        }
        return;
      }

      const redirectUri = import.meta.env.PROD ? `${CANONICAL_ORIGIN}/discord/oauth` : `${window.location.origin}/discord/oauth`;
      const scopes = ['identify', 'guilds', 'rpc.activities.write'] as const;

      const { code } = await discordSdk!.commands.authorize({
        client_id: CLIENT_ID!,
        response_type: 'code',
        scope: [...scopes],
      });

      const tokenRes = await fetch('/api/discord/token', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          code,
          redirectUri,
        }),
      });

      const tokenJson = await tokenRes.json();
      const tokenPayload = parseTokenPayload(tokenJson);
      if (!tokenRes.ok || !tokenPayload) {
        throw new Error('Discord token exchange failed');
      }
      if (tokenPayload.scope && !hasRequiredScopes(tokenPayload.scope)) {
        throw new Error('Discord scope mismatch');
      }

      applyTokenPayload(tokenPayload);
      await authenticateWithAccessToken(tokenPayload.accessToken);
    } catch (err) {
      captureError(err, { scope: 'discord', action: 'ensureDiscordAuthenticated.auth' });
      authFailed = true;
      clearAuthState(true);
      if (import.meta.env.DEV) {
        console.log('Discord auth failed, presence disabled:', err);
      }
      // Track auth failure
      trackDiscordAuthFailure(err instanceof Error ? err.message : 'unknown');
    } finally {
      authPromise = null;
    }
  })();

  await authPromise;
  return isAuthenticated;
}

// Helper to format difficulty for display
function formatDifficulty(difficulty: string): string {
  return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
}

async function syncPresence(): Promise<void> {
  if (!isDiscordReady() || !discordSdk || !currentSession) return;

  const authed = await ensureDiscordAuthenticated();
  if (!authed || !discordSdk || !currentSession) return;

  let details: string;
  let state: string | null = null;
  let detailsUrl: string | null = null;
  let assets: { large_image?: string; large_text?: string; small_image?: string; small_text?: string } | null = null;
  let timestamps: { start?: number } | null = null;

  if (currentSession.kind === 'idle') {
    details = 'Morse Hero';
    state = 'Ready to play';
    assets = { large_image: 'logo', large_text: 'Morse Hero' };
  } else if (currentSession.kind === 'levelSelect') {
    details = 'Solo';
    state = 'Choosing a message...';
    assets = { large_image: 'logo', large_text: 'Morse Hero' };
  } else if (currentSession.kind === 'solo') {
    const msgNumber = Math.max(1, Math.floor(currentSession.messageNumber));
    details = `Solo • ${formatDifficulty(currentSession.difficulty)}`;
    state = `Message ${msgNumber}`;
    assets = { large_image: 'logo', large_text: 'Morse Hero' };
    timestamps = { start: currentSession.startTime };
  } else if (currentSession.kind === 'soloResults') {
    details = 'Solo';
    state = 'Viewing results...';
    assets = { large_image: 'logo', large_text: 'Morse Hero' };
  } else if (currentSession.kind === 'daily') {
    details = 'Daily Challenge';
    state = "Attempting today's puzzle";
    assets = { large_image: 'logo', large_text: 'Morse Hero' };
    timestamps = { start: currentSession.startTime };
  } else if (currentSession.kind === 'dailyResults') {
    details = `Daily Result - ${currentSession.score.toLocaleString()} pts`;
    state = 'Viewing results...';
    assets = { large_image: 'logo', large_text: 'Morse Hero' };
  } else if (currentSession.kind === 'lobby') {
    const modeLabel = currentSession.mode === 'telephone' ? 'Telephone' : 'Co-op';
    const playerLabel = currentSession.playerCount === 1 ? '1 Player' : `${currentSession.playerCount} Players`;
    details = `${modeLabel} • ${formatDifficulty(currentSession.difficulty)} • ${playerLabel}`;
    state = 'In lobby';
    assets = { large_image: 'logo', large_text: 'Morse Hero' };
    if (currentSession.roomId) {
      const joinOrigin = import.meta.env.PROD ? CANONICAL_ORIGIN : window.location.origin;
      detailsUrl = `${joinOrigin}/?roomId=${encodeURIComponent(currentSession.roomId)}`;
    }
  } else if (currentSession.kind === 'match') {
    const modeLabel = currentSession.mode === 'telephone' ? 'Telephone' : 'Co-op';
    details = `${modeLabel} • ${formatDifficulty(currentSession.difficulty)}`;
    if (currentSession.mode === 'telephone') {
      state = `Passing messages with ${currentSession.playerCount} players`;
    } else {
      const names = currentSession.playerNames.slice(0, 3).join(', ');
      state = `In match with ${names}`;
    }
    assets = { large_image: 'logo', large_text: 'Morse Hero' };
    timestamps = { start: currentSession.startTime };
  } else if (currentSession.kind === 'multiplayerResults') {
    const modeLabel = currentSession.mode === 'telephone' ? 'Telephone' : 'Co-op';
    details = modeLabel;
    state = 'Viewing results...';
    assets = { large_image: 'logo', large_text: 'Morse Hero' };
  } else {
    // Fallback - should not happen
    details = 'Morse Hero';
    state = null;
  }

  if (import.meta.env.DEV) {
    console.log('Discord presence:', details, state, detailsUrl, assets, timestamps);
  }

  try {
    const activity: {
      type: 0;
      instance: true;
      details: string;
      state: string | null;
      details_url?: string;
      assets?: { large_image?: string; large_text?: string; small_image?: string; small_text?: string };
      timestamps?: { start?: number };
    } = {
      type: 0,
      instance: true,
      details,
      state,
    };

    if (detailsUrl) {
      activity.details_url = detailsUrl;
    }

    if (assets) {
      activity.assets = assets;
    }

    if (timestamps) {
      activity.timestamps = timestamps;
    }

    await discordSdk.commands.setActivity({
      activity,
    });
  } catch (err) {
    captureError(err, { scope: 'discord', action: 'syncPresence' });
    if (import.meta.env.DEV) {
      console.log('Failed to set Discord presence:', err);
    }
  }
}
